import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mail, Eye, AlertTriangle, Clock, CheckCircle, Users, FolderOpen, Send, RefreshCw, UserPlus, Settings, Calendar, ChevronDown, Save } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { settingsAPI, reportsAPI } from "@/services/api";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReportsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [availableRecipients, setAvailableRecipients] = useState({ osr: [], isr: [] });
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [activeTab, setActiveTab] = useState("individual");
  const [managementReport, setManagementReport] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Multi-select recipients for management report (management/admin only)
  const [selectedMgmtRecipients, setSelectedMgmtRecipients] = useState([]);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [managementRecipients, setManagementRecipients] = useState([]);

  // Email settings state
  const CADENCE_OPTIONS = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }
  ];
  const DAYS_OF_WEEK = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" }
  ];
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    cadence: "weekly",
    day_of_week: 0,
    day_of_month: 1,
    send_to_osr: true,
    send_to_isr: true
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [sendingReports, setSendingReports] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);

  // Fetch available recipients and email settings on mount
  useEffect(() => {
    fetchAvailableRecipients();
    fetchManagementRecipients();
    loadEmailSettings();
    loadEmailConfig();
  }, []);

  const loadEmailSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await settingsAPI.getEmailReports();
      if (response.data) {
        setEmailSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to load email settings:", error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const loadEmailConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/email-config`);
      const data = await response.json();
      setEmailConfig(data);
    } catch (err) {
      console.error("Failed to load email config:", err);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await settingsAPI.updateEmailReports(emailSettings);
      toast.success("Email settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendReports = async (roleFilter) => {
    setSendingReports(true);
    setReportResult(null);
    try {
      const response = await reportsAPI.sendWeekly(roleFilter);
      if (response.data.success) {
        setReportResult(response.data.results);
        const sent = response.data.results.sent.length;
        toast.success(`Sent ${sent} report${sent !== 1 ? "s" : ""} successfully!`);
      }
    } catch (error) {
      toast.error("Failed to send reports: " + (error.response?.data?.detail || error.message));
    } finally {
      setSendingReports(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/test-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: testEmail })
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Test email sent successfully!");
      } else {
        toast.error(data.detail || "Failed to send test email");
      }
    } catch (err) {
      toast.error("Failed to send test email: " + err.message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const getScheduleDescription = () => {
    if (!emailSettings.enabled) return "Auto reports are disabled";
    const dayName = DAYS_OF_WEEK.find(d => d.value === emailSettings.day_of_week)?.label || "Monday";
    switch (emailSettings.cadence) {
      case "daily": return "Reports will be sent every day";
      case "weekly": return `Reports will be sent every ${dayName}`;
      case "monthly": return `Reports will be sent on day ${emailSettings.day_of_month} of each month`;
      default: return "";
    }
  };

  const fetchAvailableRecipients = async () => {
    try {
      const response = await api.get(`${API_URL}/api/reports/available`);
      if (response.data.success) {
        setAvailableRecipients({
          osr: response.data.osr || [],
          isr: response.data.isr || []
        });
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
      toast.error("Failed to load report recipients");
    }
  };

  const fetchManagementRecipients = async () => {
    try {
      const response = await api.get(`${API_URL}/api/reports/management-recipients`);
      if (response.data.success) {
        setManagementRecipients(response.data.recipients || []);
      }
    } catch (error) {
      console.error("Error fetching management recipients:", error);
    }
  };

  const fetchPreview = async (resourceId) => {
    setLoading(true);
    setPreviewData(null);
    setPreviewHtml(null);
    
    try {
      const response = await api.get(`${API_URL}/api/reports/preview/enhanced/${resourceId}`);
      if (response.data.success) {
        setPreviewData(response.data);
        setPreviewHtml(response.data.html);
      }
    } catch (error) {
      console.error("Error fetching preview:", error);
      toast.error("Failed to generate report preview");
    } finally {
      setLoading(false);
    }
  };

  const fetchManagementReport = async () => {
    setLoading(true);
    setManagementReport(null);
    setShowRecipientSelector(false);
    setSelectedMgmtRecipients([]);
    
    try {
      const response = await api.get(`${API_URL}/api/reports/preview/management/enhanced`);
      if (response.data.success) {
        setManagementReport(response.data);
        setShowRecipientSelector(true);
      }
    } catch (error) {
      console.error("Error fetching management report:", error);
      toast.error("Failed to generate management report");
    } finally {
      setLoading(false);
    }
  };

  const handleRecipientSelect = (resourceId) => {
    const allRecipients = [...availableRecipients.osr, ...availableRecipients.isr];
    const recipient = allRecipients.find(r => r.id === resourceId);
    setSelectedRecipient(recipient);
    if (recipient) {
      fetchPreview(resourceId);
    }
  };

  const handleSendReport = async () => {
    if (!selectedRecipient) return;
    
    setSending(true);
    try {
      const response = await api.post(`${API_URL}/api/reports/send-single`, {
        resource_id: selectedRecipient.id
      });
      
      if (response.data.success) {
        toast.success(`Report sent to ${selectedRecipient.name}`);
      } else {
        toast.error(response.data.error || "Failed to send report");
      }
    } catch (error) {
      console.error("Error sending report:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send report. Check Azure permissions.";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleMgmtRecipientToggle = (recipientId) => {
    setSelectedMgmtRecipients(prev => 
      prev.includes(recipientId) 
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleSelectAllManagement = () => {
    const mgmtIds = managementRecipients.filter(r => r.email).map(r => r.id);
    const allSelected = mgmtIds.every(id => selectedMgmtRecipients.includes(id));
    if (allSelected) {
      setSelectedMgmtRecipients([]);
    } else {
      setSelectedMgmtRecipients(mgmtIds);
    }
  };

  const handleSendManagementReport = async () => {
    if (selectedMgmtRecipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post(`${API_URL}/api/reports/send-management`, {
        recipient_ids: selectedMgmtRecipients
      });
      
      if (response.data.success) {
        toast.success(`Report sent to ${response.data.sent.length} recipients`);
        if (response.data.failed?.length > 0) {
          toast.warning(`Failed to send to: ${response.data.failed.join(", ")}`);
        }
      } else {
        toast.error(response.data.error || "Failed to send reports");
      }
    } catch (error) {
      console.error("Error sending management report:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send reports. Check Azure permissions.";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const renderSummaryCards = (report) => {
    if (!report?.report) return null;
    const data = report.report;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{data.projects?.length || 0}</p>
                <p className="text-sm text-blue-600">Active Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{data.overdue_tasks?.length || 0}</p>
                <p className="text-sm text-red-600">Overdue Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{data.due_today?.length || 0}</p>
                <p className="text-sm text-amber-600">Due Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{data.due_this_week?.length || 0}</p>
                <p className="text-sm text-green-600">Due This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderRecipientSelector = () => {
    const validRecipients = managementRecipients.filter(r => r.email);
    
    return (
      <Card className="border-primary/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserPlus className="h-5 w-5" />
            Select Management Recipients
          </CardTitle>
          <CardDescription>Choose managers and admins to send this summary report to</CardDescription>
        </CardHeader>
        <CardContent>
          {validRecipients.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No management recipients found in the system.</p>
              <p className="text-sm">Management roles include: Management, PGM, Manager, Admin, Director, VP, Executive</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">Management & Admin ({validRecipients.length})</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleSelectAllManagement}
                  className="text-xs"
                >
                  {validRecipients.every(r => selectedMgmtRecipients.includes(r.id)) 
                    ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-auto">
                {validRecipients.map(recipient => (
                  <label 
                    key={recipient.id} 
                    className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer border"
                  >
                    <Checkbox 
                      checked={selectedMgmtRecipients.includes(recipient.id)}
                      onCheckedChange={() => handleMgmtRecipientToggle(recipient.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium block truncate">{recipient.name}</span>
                      <span className="text-xs text-muted-foreground truncate block">{recipient.role}</span>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
          
          {/* Send Button */}
          <div className="mt-4 pt-4 border-t flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {selectedMgmtRecipients.length} recipient{selectedMgmtRecipients.length !== 1 ? 's' : ''} selected
            </p>
            <Button 
              onClick={handleSendManagementReport}
              disabled={sending || selectedMgmtRecipients.length === 0}
              data-testid="send-mgmt-report-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to Selected ({selectedMgmtRecipients.length})
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderManagementSummary = () => {
    if (!managementReport?.report) return null;
    const data = managementReport.report;
    const summary = data.summary || {};
    
    return (
      <div className="space-y-6">
        {/* Recipient Selector - Show after report is generated */}
        {showRecipientSelector && renderRecipientSelector()}
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{summary.project_stats?.Active || 0}</p>
              <p className="text-sm text-blue-600">Active Projects</p>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-red-700">{summary.total_overdue || 0}</p>
              <p className="text-sm text-red-600">Overdue Tasks</p>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{summary.at_risk_projects || 0}</p>
              <p className="text-sm text-amber-600">At Risk Projects</p>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{summary.unassigned || 0}</p>
              <p className="text-sm text-purple-600">Unassigned Tasks</p>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-700">{summary.due_this_week || 0}</p>
              <p className="text-sm text-green-600">Due This Week</p>
            </CardContent>
          </Card>
        </div>

        {/* At Risk Projects */}
        {data.projects_at_risk?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Projects at Risk ({data.projects_at_risk.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.projects_at_risk.map((project, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="font-medium">{project.name}</p>
                      <p className="text-sm text-muted-foreground">{project.customer}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="destructive">{project.risk_reason}</Badge>
                      <p className="text-sm text-muted-foreground mt-1">OSR: {project.osr}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resource Workload */}
        {data.resource_workload?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Resource Workload
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.resource_workload.map((resource, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div>
                      <p className="font-medium">{resource.name}</p>
                      <Badge variant="outline" className="text-xs">{resource.role}</Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold">{resource.active_tasks}</p>
                      <p className="text-xs text-muted-foreground">active tasks</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* HTML Preview */}
        {managementReport.html && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Preview
              </CardTitle>
              <CardDescription>This is how the email will appear to recipients</CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="border rounded-lg p-4 bg-white max-h-[600px] overflow-auto"
                dangerouslySetInnerHTML={{ __html: managementReport.html }}
              />
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Email Reports</h1>
          <p className="text-muted-foreground">Preview and send weekly reports to team members</p>
        </div>
        <Button variant="outline" onClick={fetchAvailableRecipients}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="individual" data-testid="individual-tab">
            <Users className="h-4 w-4 mr-2" />
            Individual
          </TabsTrigger>
          <TabsTrigger value="management" data-testid="management-tab">
            <FolderOpen className="h-4 w-4 mr-2" />
            Management
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="settings-tab">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Individual Reports Tab */}
        <TabsContent value="individual" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Recipient</CardTitle>
              <CardDescription>Choose an OSR or ISR to preview their personalized report</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* OSR Select */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Outside Sales Representatives (OSR)</label>
                  <Select onValueChange={handleRecipientSelect}>
                    <SelectTrigger data-testid="osr-select">
                      <SelectValue placeholder="Select an OSR" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRecipients.osr.map((osr) => (
                        <SelectItem key={osr.id} value={osr.id}>
                          {osr.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* ISR Select */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Inside Sales Representatives (ISR)</label>
                  <Select onValueChange={handleRecipientSelect}>
                    <SelectTrigger data-testid="isr-select">
                      <SelectValue placeholder="Select an ISR" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRecipients.isr.map((isr) => (
                        <SelectItem key={isr.id} value={isr.id}>
                          {isr.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Generating report preview...</span>
            </div>
          )}

          {/* Preview Section */}
          {previewData && !loading && (
            <div className="space-y-6">
              {/* Recipient Info */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Report for {previewData.resource?.name}
                      </CardTitle>
                      <CardDescription>
                        Role: {previewData.resource?.role} | Email: {previewData.resource?.email || "Not configured"}
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={handleSendReport} 
                      disabled={sending || !previewData.resource?.email}
                      data-testid="send-report-btn"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Report
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* Summary Cards */}
              {renderSummaryCards(previewData)}

              {/* Detailed Data */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Overdue Tasks */}
                {previewData.report?.overdue_tasks?.length > 0 && (
                  <Card className="border-red-200">
                    <CardHeader className="bg-red-50">
                      <CardTitle className="text-red-700 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Overdue Tasks ({previewData.report.overdue_tasks.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {previewData.report.overdue_tasks.map((task, idx) => (
                          <div key={idx} className="p-2 bg-red-50 rounded border border-red-100">
                            <p className="font-medium text-sm">{task.name}</p>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{task.project}</span>
                              <span className="text-red-600 font-medium">{task.days_overdue} days overdue</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Due Today */}
                {previewData.report?.due_today?.length > 0 && (
                  <Card className="border-amber-200">
                    <CardHeader className="bg-amber-50">
                      <CardTitle className="text-amber-700 flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Due Today ({previewData.report.due_today.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {previewData.report.due_today.map((task, idx) => (
                          <div key={idx} className="p-2 bg-amber-50 rounded border border-amber-100">
                            <p className="font-medium text-sm">{task.name}</p>
                            <p className="text-xs text-muted-foreground">{task.project}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Due This Week */}
                {previewData.report?.due_this_week?.length > 0 && (
                  <Card className="border-blue-200">
                    <CardHeader className="bg-blue-50">
                      <CardTitle className="text-blue-700 flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Due This Week ({previewData.report.due_this_week.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {previewData.report.due_this_week.map((task, idx) => (
                          <div key={idx} className="p-2 bg-blue-50 rounded border border-blue-100">
                            <p className="font-medium text-sm">{task.name}</p>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{task.project}</span>
                              <span className="text-blue-600 font-medium">{task.days_until} days</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Active Projects */}
                {previewData.report?.projects?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FolderOpen className="h-5 w-5" />
                        Active Projects ({previewData.report.projects.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {previewData.report.projects.map((project, idx) => (
                          <div key={idx} className="p-2 bg-slate-50 rounded border">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-sm">{project.name}</p>
                                <p className="text-xs text-muted-foreground">{project.customer}</p>
                              </div>
                              <Badge variant={project.completion >= 80 ? "success" : project.completion >= 50 ? "warning" : "secondary"}>
                                {project.completion?.toFixed(0)}%
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Email HTML Preview */}
              {previewHtml && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Email Preview
                    </CardTitle>
                    <CardDescription>This is how the email will appear in the recipient's inbox</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="border rounded-lg p-4 bg-white max-h-[600px] overflow-auto"
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Empty State */}
          {!loading && !previewData && (
            <Card className="py-12">
              <CardContent className="text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Team Member</h3>
                <p className="text-muted-foreground">Choose an OSR or ISR from the dropdown to preview their personalized weekly report</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Management Summary Tab */}
        <TabsContent value="management" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Management Summary Report</CardTitle>
                  <CardDescription>Comprehensive overview of all projects and team workload</CardDescription>
                </div>
                <Button onClick={fetchManagementReport} disabled={loading} data-testid="generate-mgmt-report-btn">
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {loading && activeTab === "management" && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Generating management report...</span>
            </div>
          )}

          {managementReport && !loading && renderManagementSummary()}

          {!loading && !managementReport && (
            <Card className="py-12">
              <CardContent className="text-center">
                <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Management Report</h3>
                <p className="text-muted-foreground">Click "Generate Report" to view the comprehensive management summary</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Email Report Settings
              </CardTitle>
              <CardDescription>Configure automated email reports for OSRs and ISRs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingSettings ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium">Auto Reports</Label>
                      <p className="text-sm text-muted-foreground">{getScheduleDescription()}</p>
                    </div>
                    <Switch
                      checked={emailSettings.enabled}
                      onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, enabled: checked })}
                      data-testid="reports-toggle"
                    />
                  </div>

                  {/* Schedule Configuration */}
                  <div className={`space-y-4 ${!emailSettings.enabled ? "opacity-50 pointer-events-none" : ""}`}>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Frequency
                        </Label>
                        <Select
                          value={emailSettings.cadence}
                          onValueChange={(value) => setEmailSettings({ ...emailSettings, cadence: value })}
                        >
                          <SelectTrigger data-testid="cadence-select">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            {CADENCE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {emailSettings.cadence === "weekly" && (
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Day of Week
                          </Label>
                          <Select
                            value={String(emailSettings.day_of_week)}
                            onValueChange={(value) => setEmailSettings({ ...emailSettings, day_of_week: parseInt(value) })}
                          >
                            <SelectTrigger data-testid="day-of-week-select">
                              <SelectValue placeholder="Select day" />
                            </SelectTrigger>
                            <SelectContent>
                              {DAYS_OF_WEEK.map((day) => (
                                <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {emailSettings.cadence === "monthly" && (
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Day of Month
                          </Label>
                          <Select
                            value={String(emailSettings.day_of_month)}
                            onValueChange={(value) => setEmailSettings({ ...emailSettings, day_of_month: parseInt(value) })}
                          >
                            <SelectTrigger data-testid="day-of-month-select">
                              <SelectValue placeholder="Select day" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                                <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Recipients */}
                    <div className="space-y-3">
                      <Label>Recipients</Label>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="send-osr"
                            checked={emailSettings.send_to_osr}
                            onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, send_to_osr: checked })}
                          />
                          <Label htmlFor="send-osr" className="text-sm font-normal cursor-pointer">Send to OSRs</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id="send-isr"
                            checked={emailSettings.send_to_isr}
                            onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, send_to_isr: checked })}
                          />
                          <Label htmlFor="send-isr" className="text-sm font-normal cursor-pointer">Send to ISRs</Label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="save-settings-btn">
                      {savingSettings ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Settings
                    </Button>
                  </div>

                  {/* Manual Send Section */}
                  <Collapsible open={reportsOpen} onOpenChange={setReportsOpen}>
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <Send className="h-4 w-4" />
                          Send Reports Now
                        </span>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${reportsOpen ? "rotate-180" : ""}`} />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-4">
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">Manually trigger reports regardless of the schedule above.</p>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleSendReports(null)} disabled={sendingReports} data-testid="send-all-reports-btn">
                            {sendingReports ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                            Send All Reports
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleSendReports("OSR")} disabled={sendingReports}>OSR Only</Button>
                          <Button variant="outline" size="sm" onClick={() => handleSendReports("ISR")} disabled={sendingReports}>ISR Only</Button>
                        </div>
                        {reportResult && (
                          <div className="p-3 bg-muted rounded-lg text-sm space-y-2">
                            <div className="flex items-center gap-2 text-green-600 font-medium">
                              <CheckCircle className="h-4 w-4" />
                              Sent {reportResult.sent.length} report{reportResult.sent.length !== 1 ? "s" : ""}
                            </div>
                            {reportResult.sent.length > 0 && (
                              <div className="text-muted-foreground">
                                {reportResult.sent.map((s, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">{s.role}</Badge>
                                    <span>{s.name}</span>
                                    <span className="text-xs">({s.projects} projects, {s.overdue_tasks} overdue, {s.upcoming_tasks} upcoming)</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Test Email Section */}
                  <div className="pt-4 border-t space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium">Test Email Configuration</Label>
                        <p className="text-sm text-muted-foreground">Send a test email to verify your setup is working</p>
                      </div>
                      {emailConfig && (
                        <Badge variant={emailConfig.azure_configured ? "default" : "destructive"}>
                          {emailConfig.azure_configured ? "Configured" : "Not Configured"}
                        </Badge>
                      )}
                    </div>
                    {emailConfig && (
                      <div className="p-3 bg-muted/50 rounded-lg text-sm space-y-1">
                        <div><strong>Sender:</strong> {emailConfig.sender_address}</div>
                        <div><strong>Sender Name:</strong> {emailConfig.sender_name}</div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter email address to test"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        className="flex-1"
                        data-testid="test-email-input"
                      />
                      <Button onClick={handleSendTestEmail} disabled={sendingTestEmail || !testEmail} data-testid="send-test-email-btn">
                        {sendingTestEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Send Test
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
